// btw-front/src/pages/PartnerSignup.jsx
// 파트너 가입 신청 — 두 갈래. 둘 다 관리자 승인 후 로그인할 수 있다.
//   - 신규 회사 가입: 회사 정보 + 첨부 + 계정 + 영업대표 정보 → 승인 시 회사가 새로 등록된다
//   - 기존 회사 가입: 이미 등록된 회사를 회사명으로 검색해 고르고(회사명·대표자명 자동 표시)
//                    계정 + 영업대표 정보만 입력 → 승인 시 그 회사에 계정이 추가된다
//                    (같은 회사 계정은 모두 동등하고, 각자 자기 영업대표 목록을 가진다)
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message, Modal } from "antd";
import DaumPostcodeEmbed from "react-daum-postcode";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { Input } from "../components/ui/Input";
import { FileAttachment } from "../components/ui/FileAttachment";
import { ExistingCompanyPicker } from "../components/partner/signup/ExistingCompanyPicker";
import { usePartnerSession } from "../context/PartnerSessionContext";
// 외부 사업자번호 진위확인 API 도입 시점에 부활 — checkBusinessNumber / isValidBusinessNumber
// 는 그때 git history 에서 복구해 다시 import.
import { create as createSignup, checkLoginId } from "../api/partnerSignup";
import {
  validateSignupForm,
  validateMemberSignupForm,
  normalizeLoginId,
  LOGIN_ID_RE,
  FIELD_LABELS,
  FIELD_ORDER,
  FIELD_ORDER_MEMBER,
} from "../lib/partnerSignupValidation";
import { formatBusinessNumber, formatPhone } from "../lib/format";

const BROCHURE_ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx,.hwp,.hwpx";
const DOC_IMAGE_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic";
// 가입 유형 — 서버 requestKind 와 같은 값
const KIND_COMPANY = "company";
const KIND_MEMBER = "member";

export default function PartnerSignup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSignupToken } = usePartnerSession();
  const [kind, setKind] = useState(KIND_COMPANY);
  // 기존 회사 가입에서 고른 회사 { id, companyName, representativeName } | null
  const [memberCompany, setMemberCompany] = useState(null);
  const isMember = kind === KIND_MEMBER;

  const [form, setForm] = useState({
    loginId: "",
    companyName: "",
    businessNumber: "",
    representativeName: "",
    companyEmail: "",
    industry: "",
    address: "",
    addressDetail: "",
    salesRepHeadcount: "",
    techSupportHeadcount: "",
    contactName: "",
    contactTitle: "",
    contactEmail: "",
    contactPhone: "",
    password: "",
    passwordConfirm: "",
    termsService: false,
    termsPrivacy: false,
    termsMarketing: false,
  });
  const [brochure, setBrochure] = useState(null);
  const [brochureError, setBrochureError] = useState("");
  const [businessRegistration, setBusinessRegistration] = useState(null);
  const [businessRegistrationError, setBusinessRegistrationError] = useState("");
  const [bankbook, setBankbook] = useState(null);
  const [bankbookError, setBankbookError] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // 카카오 우편번호 검색 모달 (키 불필요·무료·호출 무제한 공식 서비스)
  const [postcodeOpen, setPostcodeOpen] = useState(false);
  // 로그인 ID 중복조회 상태: 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  const [loginIdStatus, setLoginIdStatus] = useState("idle");

  // 사업자번호 숫자만 10자리(handleSubmit 의 validation + payload 전송용)
  const bnDigits = form.businessNumber.replace(/\D/g, "").slice(0, 10);
  // existingPartner 안내 UI 는 자체 partner 테이블 중복 가입 lookup 결과로,
  // 추후 외부 사업자번호 진위확인 도입과 함께 다시 활성화 예정. 일단 항상 null.
  const existingPartner = null;

  // 로그인 ID 디바운스 중복조회 — 형식 통과 시에만 서버 호출
  useEffect(() => {
    const id = normalizeLoginId(form.loginId);
    if (!id) {
      setLoginIdStatus("idle");
      return;
    }
    if (!LOGIN_ID_RE.test(id)) {
      setLoginIdStatus("invalid");
      return;
    }
    setLoginIdStatus("checking");
    let cancelled = false;
    const handle = setTimeout(() => {
      checkLoginId(id)
        .then((r) => {
          if (cancelled) return;
          if (r && r.available) setLoginIdStatus("available");
          else if (r && r.reason === "taken") setLoginIdStatus("taken");
          else setLoginIdStatus("invalid");
        })
        .catch(() => {
          if (!cancelled) setLoginIdStatus("idle");
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [form.loginId]);

  // 사업자번호 10자리 + 체크섬 통과 시점에 서버 lookup (현재 비활성)
  // useEffect(() => {
  //   const bnDigits = form.businessNumber.replace(/\D/g, "").slice(0, 10);
  //   if (bnDigits.length !== 10 || !isValidBusinessNumber(bnDigits)) {
  //     setExistingPartner(null);
  //     return;
  //   }
  //   let cancelled = false;
  //   checkBusinessNumber(bnDigits)
  //     .then((res) => { if (!cancelled) setExistingPartner(res && res.exists ? res.partner : null); })
  //     .catch(() => { if (!cancelled) setExistingPartner(null); });
  //   return () => { cancelled = true; };
  // }, [form.businessNumber]);

  function setField(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  // errorKey → i18n 메시지 변환
  function errorsToMessages(errorKeys) {
    const out = {};
    for (const [field, key] of Object.entries(errorKeys)) {
      out[field] = t(`partners.signup.errors.${key}`);
    }
    return out;
  }

  const fieldOrder = isMember ? FIELD_ORDER_MEMBER : FIELD_ORDER;

  // 유형을 바꾸면 이전 유형의 오류 표시를 지운다(입력값은 공통 필드라 그대로 둔다)
  function changeKind(next) {
    if (next === kind) return;
    setKind(next);
    setErrors({});
  }

  function focusFirstError(errorKeys) {
    const first = fieldOrder.find((k) => errorKeys[k]);
    if (!first || first === "terms") return; // 약관은 별도 영역
    // input[name="<field>"] 또는 첫 번째 fieldset 의 input 으로 scroll
    const el = document.querySelector(`[name="${first}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") setTimeout(() => el.focus(), 250);
    }
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    if (submitting) return;
    const errorKeys = isMember
      ? validateMemberSignupForm(form, memberCompany)
      : validateSignupForm(form, bnDigits);
    if (Object.keys(errorKeys).length > 0) {
      setErrors(errorsToMessages(errorKeys));
      // 사용자가 즉시 알 수 있도록 첫 에러 필드명 + 메시지를 토스트로
      const firstField = fieldOrder.find((k) => errorKeys[k]);
      const labelKey = FIELD_LABELS[firstField];
      const fieldLabel = labelKey ? t(labelKey) : firstField;
      const errKey = errorKeys[firstField];
      const errMsg = t(`partners.signup.errors.${errKey}`);
      message.warning(`${fieldLabel}: ${errMsg}`);
      focusFirstError(errorKeys);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      if (isMember) {
        await submitMember();
        return;
      }
      const payload = {
        loginId: normalizeLoginId(form.loginId),
        companyName: form.companyName,
        businessNumber: bnDigits,
        representativeName: form.representativeName,
        companyEmail: form.companyEmail,
        industry: form.industry || null,
        address: [form.address.trim(), form.addressDetail.trim()].filter(Boolean).join(" ") || null,
        salesRepHeadcount: form.salesRepHeadcount === "" ? null : Number(form.salesRepHeadcount),
        techSupportHeadcount:
          form.techSupportHeadcount === "" ? null : Number(form.techSupportHeadcount),
        contactName: form.contactName,
        contactTitle: form.contactTitle || null,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        password: form.password,
        termsServiceAgreed: form.termsService,
        privacyConsentAgreed: form.termsPrivacy,
        marketingConsentAgreed: form.termsMarketing,
      };
      const res = await createSignup(payload, { brochure, businessRegistration, bankbook });
      setSignupToken({
        token: `tok_${Date.now()}`,
        requestId: res.id,
        companyName: form.companyName,
        contactName: form.contactName,
        contactEmail: form.contactEmail,
        matchType: existingPartner ? "existing" : "new",
        brochure: brochure
          ? { name: brochure.name, size: brochure.size, type: brochure.type }
          : null,
        createdAt: new Date().toISOString(),
      });
      message.success(t("partners.signup.doneTitle"));
      navigate("/partners/pending", { replace: true });
    } catch (err) {
      showSubmitError(err);
    } finally {
      setSubmitting(false);
    }
  }

  // 기존 회사 가입 제출 — 회사 값은 보내지 않는다(서버가 고른 회사에서 복사). 첨부도 없다.
  async function submitMember() {
    const payload = {
      requestKind: KIND_MEMBER,
      partnerId: memberCompany.id,
      loginId: normalizeLoginId(form.loginId),
      contactName: form.contactName,
      contactTitle: form.contactTitle || null,
      contactEmail: form.contactEmail,
      contactPhone: form.contactPhone,
      password: form.password,
      termsServiceAgreed: form.termsService,
      privacyConsentAgreed: form.termsPrivacy,
      marketingConsentAgreed: form.termsMarketing,
    };
    const res = await createSignup(payload, {});
    setSignupToken({
      token: `tok_${Date.now()}`,
      requestId: res.id,
      requestKind: KIND_MEMBER,
      companyName: memberCompany.companyName,
      contactName: form.contactName,
      contactEmail: form.contactEmail,
      matchType: "existing",
      brochure: null,
      createdAt: new Date().toISOString(),
    });
    message.success(t("partners.signup.doneTitle"));
    navigate("/partners/pending", { replace: true });
  }

  function showSubmitError(err) {
    // 서버 에러 메시지 우선순위:
    // 1) 우리 형식: { error: { message } }
    // 2) Spring/타 서비스 형식: { message }
    // 3) HTTP status 가 있는 일반 에러
    // 4) 네트워크 에러 등 — fallback
    const payload = err && err.payload;
    let msg =
      (payload && payload.error && payload.error.message) ||
      (payload && payload.message) ||
      (err && err.message);
    if (!msg || msg === "Failed to fetch") {
      msg = t("partners.signup.errors.serverError");
    }
    const status = err && err.status ? ` (HTTP ${err.status})` : "";
    message.error(`${msg}${status}`);
    // 콘솔에 자세한 디버그
    console.error("[signup submit] error:", err);
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
              {t("partners.signup.title")}
            </h1>
            <p className="mt-2 text-sm text-slate-600">{t("partners.signup.desc")}</p>

            <div
              role="radiogroup"
              aria-label={t("partners.signup.kind.title")}
              className="mt-6 grid gap-3 md:grid-cols-2"
            >
              <KindOption
                selected={!isMember}
                onSelect={() => changeKind(KIND_COMPANY)}
                title={t("partners.signup.kind.company")}
                desc={t("partners.signup.kind.companyDesc")}
              />
              <KindOption
                selected={isMember}
                onSelect={() => changeKind(KIND_MEMBER)}
                title={t("partners.signup.kind.member")}
                desc={t("partners.signup.kind.memberDesc")}
              />
            </div>

            <form className="mt-8 grid gap-8" onSubmit={handleSubmit} noValidate>
              {/* 계정 — 폼 최상단. 로그인 식별자(아이디) 우선 결정 */}
              <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("partners.signup.sectionAccount")}
                </legend>
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    name="loginId"
                    label={t("partners.signup.fields.loginId")}
                    required
                    placeholder={t("partners.signup.fields.loginIdPlaceholder")}
                    value={form.loginId}
                    onChange={(e) => setField("loginId", e.target.value.toLowerCase())}
                    error={errors.loginId}
                    hint={
                      loginIdStatus === "checking"
                        ? t("partners.signup.loginIdStatus.checking")
                        : loginIdStatus === "available"
                          ? t("partners.signup.loginIdStatus.available")
                          : loginIdStatus === "taken"
                            ? t("partners.signup.loginIdStatus.taken")
                            : loginIdStatus === "invalid"
                              ? t("partners.signup.loginIdStatus.invalid")
                              : t("partners.signup.fields.loginIdHint")
                    }
                    autoComplete="username"
                    maxLength={30}
                  />
                  <div />
                  <Input
                    name="password"
                    label={t("partners.signup.fields.password")}
                    required
                    type="password"
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    error={errors.password}
                    hint={t("partners.signup.fields.passwordHint")}
                    autoComplete="new-password"
                  />
                  <Input
                    name="passwordConfirm"
                    label={t("partners.signup.fields.passwordConfirm")}
                    required
                    type="password"
                    value={form.passwordConfirm}
                    onChange={(e) => setField("passwordConfirm", e.target.value)}
                    error={errors.passwordConfirm}
                    autoComplete="new-password"
                  />
                </div>
              </fieldset>

              {/* 기존 회사 가입 — 등록된 회사 선택(회사 정보·첨부 입력 없음) */}
              {isMember && (
                <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("partners.signup.member.sectionCompany")}
                  </legend>
                  <ExistingCompanyPicker
                    value={memberCompany}
                    onChange={(c) => {
                      setMemberCompany(c);
                      setErrors((er) => ({ ...er, partnerId: undefined }));
                    }}
                    error={errors.partnerId}
                  />
                </fieldset>
              )}

              {/* 회사 정보 — 신규 회사 가입만 */}
              {!isMember && (
                <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("partners.signup.sectionCompany")}
                  </legend>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      name="companyName"
                      label={t("partners.signup.fields.companyName")}
                      required
                      value={form.companyName}
                      onChange={(e) => setField("companyName", e.target.value)}
                      error={errors.companyName}
                      maxLength={100}
                    />
                    <Input
                      name="businessNumber"
                      label={t("partners.signup.fields.businessNumber")}
                      required
                      placeholder={t("partners.signup.fields.businessNumberPlaceholder")}
                      value={form.businessNumber}
                      onChange={(e) =>
                        setField("businessNumber", formatBusinessNumber(e.target.value))
                      }
                      error={errors.businessNumber}
                      inputMode="numeric"
                      maxLength={12}
                    />
                    <Input
                      name="representativeName"
                      label={t("partners.signup.fields.representativeName")}
                      required
                      value={form.representativeName}
                      onChange={(e) => setField("representativeName", e.target.value)}
                      error={errors.representativeName}
                      maxLength={50}
                    />
                    <Input
                      name="companyEmail"
                      label={t("partners.signup.fields.companyEmail")}
                      required
                      type="email"
                      value={form.companyEmail}
                      onChange={(e) => setField("companyEmail", e.target.value)}
                      error={errors.companyEmail}
                      maxLength={190}
                      autoComplete="email"
                    />
                    <Input
                      label={t("partners.signup.fields.industry")}
                      value={form.industry}
                      onChange={(e) => setField("industry", e.target.value)}
                      maxLength={50}
                    />
                    <div className="flex flex-col gap-1.5 md:col-span-2">
                      <label className="text-xs font-medium text-slate-700">
                        {t("partners.signup.fields.address")}
                      </label>
                      <div className="flex gap-2">
                        <Input
                          className="min-w-0 flex-1"
                          value={form.address}
                          placeholder={t("partners.signup.fields.addressPlaceholder")}
                          readOnly
                          onClick={() => setPostcodeOpen(true)}
                          inputClassName="cursor-pointer bg-slate-50"
                        />
                        <button
                          type="button"
                          className="btw-btn-ghost shrink-0"
                          onClick={() => setPostcodeOpen(true)}
                        >
                          {t("partners.signup.fields.addressSearch")}
                        </button>
                      </div>
                      <Input
                        value={form.addressDetail}
                        onChange={(e) => setField("addressDetail", e.target.value)}
                        placeholder={t("partners.signup.fields.addressDetailPlaceholder")}
                        maxLength={100}
                        disabled={!form.address}
                      />
                    </div>
                    <Input
                      label={t("partners.signup.fields.salesRepHeadcount")}
                      placeholder={t("partners.signup.fields.headcountPlaceholder")}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={5}
                      value={form.salesRepHeadcount}
                      onChange={(e) =>
                        setField("salesRepHeadcount", e.target.value.replace(/\D/g, "").slice(0, 5))
                      }
                    />
                    <Input
                      label={t("partners.signup.fields.techSupportHeadcount")}
                      placeholder={t("partners.signup.fields.headcountPlaceholder")}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={5}
                      value={form.techSupportHeadcount}
                      onChange={(e) =>
                        setField(
                          "techSupportHeadcount",
                          e.target.value.replace(/\D/g, "").slice(0, 5),
                        )
                      }
                    />
                  </div>
                  {existingPartner && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      {t("partners.signup.existingPartnerNotice")} ·{" "}
                      <span className="font-semibold">{existingPartner.companyName}</span>
                    </div>
                  )}

                  <FileAttachment
                    className="mt-4"
                    label={t("partners.signup.fields.companyBrochure")}
                    hint={t("partners.signup.fields.companyBrochureHint")}
                    accept={BROCHURE_ACCEPT}
                    value={brochure}
                    onChange={(f) => {
                      setBrochure(f);
                      setBrochureError("");
                    }}
                    onError={(reason) =>
                      setBrochureError(
                        reason === "size"
                          ? t("partners.signup.errors.fileSize")
                          : t("partners.signup.errors.fileType"),
                      )
                    }
                    error={brochureError}
                    buttonLabel={t("partners.signup.fields.attach")}
                    attachedLabel={t("partners.signup.fields.attached")}
                    removeLabel={t("partners.signup.fields.removeFile")}
                  />
                  <FileAttachment
                    className="mt-3"
                    label={t("partners.signup.fields.businessRegistration")}
                    hint={t("partners.signup.fields.docImageHint")}
                    accept={DOC_IMAGE_ACCEPT}
                    value={businessRegistration}
                    onChange={(f) => {
                      setBusinessRegistration(f);
                      setBusinessRegistrationError("");
                    }}
                    onError={(reason) =>
                      setBusinessRegistrationError(
                        reason === "size"
                          ? t("partners.signup.errors.fileSize")
                          : t("partners.signup.errors.fileType"),
                      )
                    }
                    error={businessRegistrationError}
                    buttonLabel={t("partners.signup.fields.attach")}
                    attachedLabel={t("partners.signup.fields.attached")}
                    removeLabel={t("partners.signup.fields.removeFile")}
                  />
                  <FileAttachment
                    className="mt-3"
                    label={t("partners.signup.fields.bankbook")}
                    hint={t("partners.signup.fields.docImageHint")}
                    accept={DOC_IMAGE_ACCEPT}
                    value={bankbook}
                    onChange={(f) => {
                      setBankbook(f);
                      setBankbookError("");
                    }}
                    onError={(reason) =>
                      setBankbookError(
                        reason === "size"
                          ? t("partners.signup.errors.fileSize")
                          : t("partners.signup.errors.fileType"),
                      )
                    }
                    error={bankbookError}
                    buttonLabel={t("partners.signup.fields.attach")}
                    attachedLabel={t("partners.signup.fields.attached")}
                    removeLabel={t("partners.signup.fields.removeFile")}
                  />
                </fieldset>
              )}

              {/* 영업대표 정보 — 승인 시 이 계정의 첫 영업대표로 등록 */}
              <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("partners.signup.sectionContact")}
                </legend>
                {isMember && (
                  <p className="mb-4 px-1 text-xs text-slate-500">
                    {t("partners.signup.member.contactHint")}
                  </p>
                )}
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    name="contactName"
                    label={t("partners.signup.fields.contactName")}
                    required
                    value={form.contactName}
                    onChange={(e) => setField("contactName", e.target.value)}
                    error={errors.contactName}
                    maxLength={50}
                  />
                  <Input
                    label={t("partners.signup.fields.contactTitle")}
                    value={form.contactTitle}
                    onChange={(e) => setField("contactTitle", e.target.value)}
                    maxLength={50}
                  />
                  <Input
                    name="contactPhone"
                    label={t("partners.signup.fields.contactPhone")}
                    required
                    placeholder={t("partners.signup.fields.contactPhonePlaceholder")}
                    value={form.contactPhone}
                    onChange={(e) => setField("contactPhone", formatPhone(e.target.value))}
                    error={errors.contactPhone}
                    inputMode="numeric"
                    maxLength={13}
                  />
                  <Input
                    name="contactEmail"
                    label={t("partners.signup.fields.contactEmail")}
                    required
                    type="email"
                    value={form.contactEmail}
                    onChange={(e) => setField("contactEmail", e.target.value)}
                    error={errors.contactEmail}
                    maxLength={120}
                  />
                </div>
              </fieldset>

              {/* 약관 */}
              <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("partners.signup.sectionTerms")}
                </legend>
                <div className="grid gap-2.5 text-sm text-slate-800">
                  <TermsRow
                    checked={form.termsService}
                    onChange={(v) => setField("termsService", v)}
                    label={t("partners.signup.terms.service")}
                    onView={() => message.info(t("partners.signup.terms.viewSoon"))}
                  />
                  <TermsRow
                    checked={form.termsPrivacy}
                    onChange={(v) => setField("termsPrivacy", v)}
                    label={t("partners.signup.terms.privacy")}
                    onView={() => message.info(t("partners.signup.terms.viewSoon"))}
                  />
                  <TermsRow
                    checked={form.termsMarketing}
                    onChange={(v) => setField("termsMarketing", v)}
                    label={t("partners.signup.terms.marketing")}
                    onView={() => message.info(t("partners.signup.terms.viewSoon"))}
                  />
                </div>
                {errors.terms && <p className="mt-2 text-xs text-rose-600">{errors.terms}</p>}
              </fieldset>

              <div className="flex flex-wrap items-center justify-end gap-3">
                <Link to="/partners" className="btw-btn-ghost">
                  {t("partners.deals.detail.cancel")}
                </Link>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btw-btn-accent disabled:opacity-60"
                >
                  {submitting ? t("partners.signup.submitting") : t("partners.signup.submit")}
                </button>
              </div>
            </form>
          </div>
        </Container>
      </main>
      <Footer />
      <Modal
        open={postcodeOpen}
        onCancel={() => setPostcodeOpen(false)}
        footer={null}
        title={t("partners.signup.fields.addressSearchTitle")}
        destroyOnClose
      >
        <DaumPostcodeEmbed
          onComplete={(data) => {
            // 사용자가 고른 유형(도로명 R / 지번 J)의 주소 + 우편번호로 채움
            const base = data.userSelectedType === "J" ? data.jibunAddress : data.roadAddress;
            setField("address", `(${data.zonecode}) ${base}`);
            setPostcodeOpen(false);
          }}
          style={{ height: 460 }}
        />
      </Modal>
    </div>
  );
}

// 가입 유형 선택 카드 — 라디오 역할(키보드 Tab·Space 로도 선택)
function KindOption({ selected, onSelect, title, desc }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={`rounded-2xl border p-4 text-left transition md:p-5 ${
        selected
          ? "border-accent bg-white ring-2 ring-accent/30"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <span className="block text-sm font-semibold text-slate-900">{title}</span>
      <span className="mt-1 block text-xs leading-relaxed text-slate-500">{desc}</span>
    </button>
  );
}

function TermsRow({ checked, onChange, label, onView }) {
  const { t } = useTranslation();
  return (
    <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent/40"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="flex-1 text-sm">{label}</span>
      <button
        type="button"
        className="text-xs text-slate-500 underline-offset-2 hover:text-accent hover:underline"
        onClick={(e) => {
          e.preventDefault();
          onView();
        }}
      >
        {t("partners.signup.terms.viewDetail")}
      </button>
    </label>
  );
}
